import React from 'react';
import {
  CampaignActionItem,
  CampaignDelivery,
  CampaignDeliveryStepStatus,
  CampaignDeliveryTarget,
  actionItemStatusLabel,
  statusLabel,
  stepKindLabel,
  stepStatusLabel,
} from '../../../../../support/campaigns';
import { useFetchActionItems, useFetchDeliveryStepStatuses } from '../../../../../hooks/campaign-hooks';
import { fmtDate } from '../../../../../support/campaignAnalytics';
import { StatusPill } from '../../builder/primitives';
import { toneForStatus } from '../statusTone';
import { DefinitionList } from '../detailPrimitives';
import { Column, MasterTable, Panel, dashboardCss as css } from '../dashboardParts';

/**
 * The deepest level: a single customer/opportunity within a delivery. Reuses the delivery's step
 * statuses and action items (already cached by react-query) and filters them to this target — the
 * step activity and follow-ups that belong to this specific account. Delivery-wide rows (no target)
 * are included on the steps, since they apply to every customer in the delivery.
 */
const TargetView: React.FC<{
  delivery: CampaignDelivery;
  target: CampaignDeliveryTarget;
}> = ({ delivery, target }) => {
  const { data: statuses } = useFetchDeliveryStepStatuses(delivery.id);
  const { data: actions } = useFetchActionItems(delivery.id);

  const steps = React.useMemo(
    () => (statuses ?? []).filter((s) => s.target == null || s.target === target.id),
    [statuses, target.id]
  );
  const items = React.useMemo(
    () => (actions ?? []).filter((a) => a.target === target.id),
    [actions, target.id]
  );

  const stepCols: Column<CampaignDeliveryStepStatus>[] = [
    { key: 'step', header: 'Step', render: (s) => s.step_name ?? `Step #${s.step}` },
    { key: 'kind', header: 'Kind', render: (s) => stepKindLabel(s.step_kind) },
    { key: 'scope', header: 'Scope', render: (s) => (s.target == null ? 'Delivery-wide' : 'This customer') },
    {
      key: 'status',
      header: 'Status',
      render: (s) => <StatusPill tone={toneForStatus(s.status)}>{stepStatusLabel(s.status)}</StatusPill>,
    },
    { key: 'completed', header: 'Completed', render: (s) => fmtDate(s.completed_at) },
  ];

  const actionCols: Column<CampaignActionItem>[] = [
    { key: 'text', header: 'Item', render: (a) => a.text },
    { key: 'kind', header: 'Kind', render: (a) => (a.kind === 'followup' ? 'Follow-up' : 'Action item') },
    { key: 'owner', header: 'Owner', render: (a) => a.owner ?? '—' },
    { key: 'due', header: 'Due', render: (a) => fmtDate(a.due_date) },
    {
      key: 'status',
      header: 'Status',
      render: (a) => <StatusPill tone={toneForStatus(a.status)}>{actionItemStatusLabel(a.status)}</StatusPill>,
    },
  ];

  return (
    <>
      <Panel title="Customer / opportunity" full>
        <div className={css.detailPad} style={{ paddingLeft: 0, paddingRight: 0 }}>
          <DefinitionList
            items={[
              ['Account', target.customer_name ?? target.customer ?? '—'],
              ['Account Id', target.customer ?? '—'],
              ['Opportunity', target.opportunity ?? '—'],
              [
                'Status',
                <StatusPill key="s" tone={toneForStatus(target.status)}>
                  {statusLabel(target.status)}
                </StatusPill>,
              ],
              ['Delivery', delivery.name ?? `Delivery #${delivery.id}`],
            ]}
          />
        </div>
      </Panel>

      <Panel title="Steps" count={steps.length} flush full>
        <MasterTable
          columns={stepCols}
          rows={steps}
          getRowId={(s) => s.id}
          empty="No step activity for this customer yet."
        />
      </Panel>

      <Panel title="Action items & follow-ups" count={items.length} flush full>
        <MasterTable
          columns={actionCols}
          rows={items}
          getRowId={(a) => a.id}
          empty="No action items for this customer."
        />
      </Panel>
    </>
  );
};

export default TargetView;
